// GENERATED FILE — do not edit. Source of truth: contracts/. Regenerate: node scripts/sync-contracts.mjs

import { api } from '../apiClient';

import { z } from 'zod';
// Runtime validation: every named type above has a matching `<Name>Schema` zod export — the
// exact schema the backend validates with (story 2-92), so forms can `.parse()`/`.safeParse()`
// against it instead of hand-rolling checks. `.refine()` cross-field rules are preserved
// verbatim; the refine() error MESSAGE is not — the server owns user-facing text.

export type FeatureRequest = {
  id: string;
  organizationId: string;
  featureKey: string;
  requestedBy: string;
  requestedByName: string;
  note?: string | null | undefined;
  status: "pending" | "granted" | "denied";
  createdAt: string;
};
export const FeatureRequestSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  featureKey: z.string(),
  requestedBy: z.string(),
  requestedByName: z.string(),
  note: z.string().nullable().optional(),
  status: z.enum(["pending", "granted", "denied"] as const),
  createdAt: z.string(),
}).strict();

export type FeatureRequestQueueItem = {
  id: string;
  organizationId: string;
  featureKey: string;
  requestedBy: string;
  requestedByName: string;
  note?: string | null | undefined;
  status: "pending" | "granted" | "denied";
  createdAt: string;
  organizationName?: string | null | undefined;
  featureName?: string | null | undefined;
};
export const FeatureRequestQueueItemSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  featureKey: z.string(),
  requestedBy: z.string(),
  requestedByName: z.string(),
  note: z.string().nullable().optional(),
  status: z.enum(["pending", "granted", "denied"] as const),
  createdAt: z.string(),
  organizationName: z.string().nullable().optional(),
  featureName: z.string().nullable().optional(),
}).strict();

export type CreateFeatureRequestResponse = FeatureRequest;

export const CreateFeatureRequestBodySchema = z.object({
  featureKey: z.string().min(3).max(60),
  note: z.string().max(400).nullable().optional(),
}).strict();

export async function createFeatureRequest(body: {
  featureKey: string;
  note?: string | null | undefined;
}): Promise<CreateFeatureRequestResponse> {
  const response = await api.post<CreateFeatureRequestResponse>(`/me/feature-requests`, body);
  return response.data;
}

export type ListFeatureRequestsResponse = {
  items: Array<FeatureRequestQueueItem>;
  nextCursor: string | null;
  total?: number | undefined;
};

export const ListFeatureRequestsQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  sort: z.string().min(1).optional(),
});

export async function listFeatureRequests(query?: {
  cursor?: string | undefined;
  limit?: number | undefined;
  sort?: string | undefined;
}): Promise<ListFeatureRequestsResponse> {
  const response = await api.get<ListFeatureRequestsResponse>(`/platform/feature-requests`, { params: query });
  return response.data;
}

export type ResolveFeatureRequestResponse = FeatureRequest;

export const ResolveFeatureRequestParamsSchema = z.object({
  id: z.string(),
});

export const ResolveFeatureRequestBodySchema = z.object({
  grant: z.boolean(),
  note: z.string().max(400).nullable().optional(),
}).strict();

export async function resolveFeatureRequest(id: string, body: {
  grant: boolean;
  note?: string | null | undefined;
}): Promise<ResolveFeatureRequestResponse> {
  const response = await api.post<ResolveFeatureRequestResponse>(`/platform/feature-requests/${encodeURIComponent(id)}/resolve`, body);
  return response.data;
}

export const featureRequestsKeys = {
  all: ["featureRequests"] as const,
  lists: () => [...featureRequestsKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...featureRequestsKeys.lists(), ...(filters ? [filters] : [])] as const,
  details: () => [...featureRequestsKeys.all, 'detail'] as const,
  detail: (id: string) => [...featureRequestsKeys.details(), id] as const,
};
